import React from "react";
import BannerImage from "../assets/home-banner-image.png";
import Logo from "../assets/idea centered inquiry_logo.png";
import ProductPlaceholderAI from "../assets/index-homepage/1.jpg";
import ProductPlaceholderUI from "../assets/index-homepage/2.png";
import ProductPlaceholderCommunity from "../assets/index-homepage/3.png";
import SolutionPlaceholderResearch from "../assets/index-homepage/4.jpeg";
import SolutionPlaceholderEnterprise from "../assets/index-homepage/5.jpeg";
import SolutionPlaceholderEducation from "../assets/index-homepage/6.png";
import PricingPlaceholder from "../assets/index-homepage/7.jpeg";
import StoryPlaceholderTheory from "../assets/index-homepage/8.png";
import StoryPlaceholderLab from "../assets/index-homepage/9.jpeg";
import { FiArrowRight, FiCheck, FiUsers, FiTrendingUp, FiShield, FiZap, FiStar, FiUser, FiCpu, FiMessageSquare, FiMenu, FiX, FiGlobe } from "react-icons/fi";
import { Register } from "../components/Register";
import { Login } from "../components/Login";
import GoogleLoginButton from "../components/GoogleLoginButton";
import { FaUsers, FaLightbulb, FaGraduationCap, FaHandshake, FaComments, FaChartLine, FaSearch } from "react-icons/fa";
import { useState, useEffect, useRef } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../i18n";
import "./Home.css";
import "./HomeTabs.css";
import "./Product.css";
import "./Solutions.css";
import "./Pricing.css";
import "./Story.css";

const LanguageSwitcher = () => {
    const { i18n, t } = useTranslation('home');
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentLabel = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language)?.label
        || SUPPORTED_LANGUAGES.find(l => i18n.language.startsWith(l.code.split('-')[0]))?.label
        || SUPPORTED_LANGUAGES[0].label;

    return (
        <div ref={ref} className="lang-switcher-wrapper">
            <button
                onClick={() => setOpen(prev => !prev)}
                title={t('language.switch')}
                className={`lang-switcher-trigger${open ? ' lang-switcher-trigger--open' : ''}`}
            >
                <FiGlobe size={14} />
                <span>{currentLabel}</span>
            </button>

            {open && (
                <div className="lang-switcher-dropdown">
                    {SUPPORTED_LANGUAGES.map(({ code, label }) => {
                        const isActive = i18n.language === code || i18n.language.startsWith(code.split('-')[0]);
                        return (
                            <button
                                key={code}
                                onClick={() => { i18n.changeLanguage(code); setOpen(false); }}
                                className={`lang-switcher-option${isActive ? ' lang-switcher-option--active' : ''}`}
                            >
                                <span>{label}</span>
                                {isActive && <FiCheck size={12} />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const LoginButtons = ({ setOpenLogin, setOpenRegister, t }) => (
    <div className="login-buttons-container">
        <GoogleLoginButton />
        <details className="login-details" style={{display: "block"}} open>
            <summary className="login-summary">{t('login.otherMethods')}</summary>
            <div className="login-options">
                <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={() => setOpenLogin(true)}
                    className="login-option-button"
                >
                    {t('login.local')}
                </Button>
                <Button
                    variant="text"
                    color="primary"
                    fullWidth
                    onClick={() => setOpenRegister(true)}
                    className="login-option-button secondary"
                >
                    {t('login.register')}
                </Button>
            </div>
        </details>
    </div>
);

export default function Home() {
    const { t } = useTranslation('home');
    const [openLogin, setOpenLogin] = useState(false);
    const [openRegister, setOpenRegister] = useState(false);
    const [activeTab, setActiveTab] = useState('home');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setMobileMenuOpen(false);
    };

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [mobileMenuOpen]);

    return (
        <div className="home-container">
            {mobileMenuOpen && (
                <div
                    className="mobile-menu-overlay"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Navigation */}
            <nav className="main-navigation">
                <div className="nav-logo-container" onClick={() => handleTabChange('home')}>
                    <img src={Logo} alt="Idea Centered Inquiry Logo" />
                </div>

                <button
                    className="mobile-menu-toggle"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    aria-label="Toggle menu"
                >
                    {mobileMenuOpen ? <FiX /> : <FiMenu />}
                </button>

                <div className={`nav-tabs ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                    <button className={`nav-tab ${activeTab === 'home' ? 'active' : ''}`} onClick={() => handleTabChange('home')}>
                        {t('nav.home')}
                    </button>
                    <button className={`nav-tab ${activeTab === 'product' ? 'active' : ''}`} onClick={() => handleTabChange('product')}>
                        {t('nav.product')}
                    </button>
                    <button className={`nav-tab ${activeTab === 'solutions' ? 'active' : ''}`} onClick={() => handleTabChange('solutions')}>
                        {t('nav.solutions')}
                    </button>
                    <button className={`nav-tab ${activeTab === 'pricing' ? 'active' : ''}`} onClick={() => handleTabChange('pricing')}>
                        {t('nav.pricing')}
                    </button>
                    <button className={`nav-tab ${activeTab === 'story' ? 'active' : ''}`} onClick={() => handleTabChange('story')}>
                        {t('nav.story')}
                    </button>
                </div>

                <LanguageSwitcher />
            </nav>

            <div className="content-wrapper">
                {/* Home Tab */}
                {activeTab === 'home' && (
                    <>
                        <section className="hero-section">
                            <div className="hero-content">
                                <div className="hero-badges">
                                    <div className="badge">
                                        <FiTrendingUp />
                                        <span>{t('hero.badge1')}</span>
                                    </div>
                                    <div className="badge">
                                        <FiShield />
                                        <span>{t('hero.badge2')}</span>
                                    </div>
                                </div>

                                <h1 className="primary-heading">
                                    <p className="original-text">{t('hero.heading1')}</p>
                                    <p className="important-text">{t('hero.heading2')}</p>
                                </h1>

                                <div className="social-proof">
                                    <div className="proof-item">
                                        <FiUsers />
                                        <span dangerouslySetInnerHTML={{ __html: t('hero.proof1') }} />
                                    </div>
                                    <div className="proof-item">
                                        <FiZap />
                                        <span dangerouslySetInnerHTML={{ __html: t('hero.proof2') }} />
                                    </div>
                                </div>

                                <div className="button-group">
                                    <div className="divider">
                                        <span>{t('hero.divider')}</span>
                                    </div>
                                    <LoginButtons setOpenLogin={setOpenLogin} setOpenRegister={setOpenRegister} t={t} />
                                </div>

                                <div className="trust-indicators">
                                    <div className="trust-item"><FiCheck /><span>{t('hero.trust1')}</span></div>
                                    <div className="trust-item"><FiCheck /><span>{t('hero.trust2')}</span></div>
                                    <div className="trust-item"><FiCheck /><span>{t('hero.trust3')}</span></div>
                                    <div className="trust-item"><FiCheck /><span>{t('hero.trust4')}</span></div>
                                </div>
                            </div>

                            <div className="hero-image">
                                <div className="image-wrapper">
                                    <img src={BannerImage} alt="Platform Interface" />
                                    <div className="floating-card card-1">
                                        <div className="card-content">
                                            <FiTrendingUp className="card-icon" />
                                            <span>{t('hero.floatingCard1')}</span>
                                        </div>
                                    </div>
                                    <div className="floating-card card-4">
                                        <div className="card-content">
                                            <FiCheck className="card-icon" />
                                            <span>{t('hero.floatingCard2')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="announcement-bar">
                                <span>{t('hero.announcement')}</span>
                            </div>
                        </section>
                    </>
                )}

                {/* Product Tab */}
                {activeTab === "product" && (
                    <section className="product-section tab-content">
                        <div className="product-header">
                            <p className="tab-slogan">{t('product.slogan')}</p>
                            <h2 className="section-title">{t('product.title')}</h2>
                        </div>

                        {/* AI Partners */}
                        <div className="product-block">
                            <div className="product-block-media">
                                <img src={ProductPlaceholderAI} alt="AI Expert Partners" />
                            </div>
                            <div className="product-block-content">
                                <h3 className="product-block-title">{t('product.ai.title')}</h3>
                                <p className="product-block-lead" dangerouslySetInnerHTML={{ __html: t('product.ai.lead') }} />
                                <div className="product-engine-row">
                                    <div className="product-engine-part">
                                        <h4>{t('product.ai.setting3')}</h4>
                                        <div className="product-engine-items">
                                            <span className="product-engine-item">{t('product.ai.personality')}</span>
                                            <span className="product-engine-sep">+</span>
                                            <span className="product-engine-item">{t('product.ai.cognition')}</span>
                                            <span className="product-engine-sep">+</span>
                                            <span className="product-engine-item">{t('product.ai.behavior')}</span>
                                        </div>
                                    </div>
                                    <div className="product-engine-part">
                                        <h4>{t('product.ai.loop2')}</h4>
                                        <div className="product-engine-items">
                                            <span className="product-engine-item">{t('product.ai.think')}</span>
                                            <span className="product-engine-sep">→</span>
                                            <span className="product-engine-item">{t('product.ai.act')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Multi-modal UI */}
                        <div className="product-block">
                            <div className="product-block-media">
                                <img src={ProductPlaceholderUI} alt="Multi-modal UI" />
                            </div>
                            <div className="product-block-content">
                                <h3 className="product-block-title">{t('product.ui.title')}</h3>
                                <p className="product-block-lead">{t('product.ui.lead')}</p>
                                <div className="product-card-grid">
                                    <div className="product-card">
                                        <FiMessageSquare className="product-card-icon" />
                                        <h4>{t('product.ui.sync.title')}</h4>
                                        <p>{t('product.ui.sync.desc')}</p>
                                    </div>
                                    <div className="product-card">
                                        <FiMessageSquare className="product-card-icon" />
                                        <h4>{t('product.ui.async.title')}</h4>
                                        <p>{t('product.ui.async.desc')}</p>
                                    </div>
                                    <div className="product-card">
                                        <FiMessageSquare className="product-card-icon" />
                                        <h4>{t('product.ui.topic.title')}</h4>
                                        <p>{t('product.ui.topic.desc')}</p>
                                    </div>
                                    <div className="product-card">
                                        <FiMessageSquare className="product-card-icon" />
                                        <h4>{t('product.ui.deep.title')}</h4>
                                        <p>{t('product.ui.deep.desc')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Hybrid Community */}
                        <div className="product-block">
                            <div className="product-block-media">
                                <img src={ProductPlaceholderCommunity} alt="Hybrid Community" />
                            </div>
                            <div className="product-block-content">
                                <h3 className="product-block-title">{t('product.community.title')}</h3>
                                <p className="product-block-lead" dangerouslySetInnerHTML={{ __html: t('product.community.lead') }} />
                                <div className="product-card-grid">
                                    <div className="product-card">
                                        <FiUser className="product-card-icon" />
                                        <h4>{t('product.community.facilitator.title')}</h4>
                                        <p>{t('product.community.facilitator.desc')}</p>
                                    </div>
                                    <div className="product-card">
                                        <FiShield className="product-card-icon" />
                                        <h4>{t('product.community.devil.title')}</h4>
                                        <p>{t('product.community.devil.desc')}</p>
                                    </div>
                                    <div className="product-card">
                                        <FiUsers className="product-card-icon" />
                                        <h4>{t('product.community.peer.title')}</h4>
                                        <p>{t('product.community.peer.desc')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Solutions Tab */}
                {activeTab === "solutions" && (
                    <section className="solutions-section tab-content">
                        <div className="solutions-header">
                            <p className="tab-slogan">{t('solutions.slogan')}</p>
                            <h2 className="section-title">{t('solutions.title')}</h2>
                        </div>

                        {/* Research */}
                        <div className="solution-category">
                            <div className="solution-category-header">
                                <FaLightbulb className="solution-category-icon" />
                                <h3>{t('solutions.research.label')}</h3>
                            </div>
                            <div className="solution-category-media">
                                <img src={SolutionPlaceholderResearch} alt="Academic Research" />
                            </div>
                            <div className="solution-scenario-grid">
                                <div className="solution-scenario-card">
                                    <h4>{t('solutions.research.sceneA.title')}</h4>
                                    <div className="solution-scenario-content">
                                        <div className="solution-pain" dangerouslySetInnerHTML={{ __html: t('solutions.research.sceneA.pain') }} />
                                        <div className="solution-solution" dangerouslySetInnerHTML={{ __html: t('solutions.research.sceneA.solution') }} />
                                    </div>
                                </div>
                                <div className="solution-scenario-card">
                                    <h4>{t('solutions.research.sceneB.title')}</h4>
                                    <div className="solution-scenario-content">
                                        <div className="solution-pain" dangerouslySetInnerHTML={{ __html: t('solutions.research.sceneB.pain') }} />
                                        <div className="solution-solution" dangerouslySetInnerHTML={{ __html: t('solutions.research.sceneB.solution') }} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enterprise */}
                        <div className="solution-category">
                            <div className="solution-category-header">
                                <FaHandshake className="solution-category-icon" />
                                <h3>{t('solutions.enterprise.label')}</h3>
                            </div>
                            <div className="solution-category-media">
                                <img src={SolutionPlaceholderEnterprise} alt="Enterprise Strategy" />
                            </div>
                            <div className="solution-scenario-grid">
                                <div className="solution-scenario-card">
                                    <h4>{t('solutions.enterprise.sceneA.title')}</h4>
                                    <div className="solution-scenario-content">
                                        <div className="solution-pain" dangerouslySetInnerHTML={{ __html: t('solutions.enterprise.sceneA.pain') }} />
                                        <div className="solution-solution" dangerouslySetInnerHTML={{ __html: t('solutions.enterprise.sceneA.solution') }} />
                                    </div>
                                </div>
                                <div className="solution-scenario-card">
                                    <h4>{t('solutions.enterprise.sceneB.title')}</h4>
                                    <div className="solution-scenario-content">
                                        <div className="solution-pain" dangerouslySetInnerHTML={{ __html: t('solutions.enterprise.sceneB.pain') }} />
                                        <div className="solution-solution" dangerouslySetInnerHTML={{ __html: t('solutions.enterprise.sceneB.solution') }} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Education */}
                        <div className="solution-category">
                            <div className="solution-category-header">
                                <FaGraduationCap className="solution-category-icon" />
                                <h3>{t('solutions.education.label')}</h3>
                            </div>
                            <div className="solution-category-media">
                                <img src={SolutionPlaceholderEducation} alt="Education" />
                            </div>
                            <div className="solution-scenario-grid">
                                <div className="solution-scenario-card">
                                    <h4>{t('solutions.education.sceneA.title')}</h4>
                                    <div className="solution-scenario-content">
                                        <div className="solution-pain" dangerouslySetInnerHTML={{ __html: t('solutions.education.sceneA.pain') }} />
                                        <div className="solution-solution" dangerouslySetInnerHTML={{ __html: t('solutions.education.sceneA.solution') }} />
                                    </div>
                                </div>
                                <div className="solution-scenario-card">
                                    <h4>{t('solutions.education.sceneB.title')}</h4>
                                    <div className="solution-scenario-content">
                                        <div className="solution-pain" dangerouslySetInnerHTML={{ __html: t('solutions.education.sceneB.pain') }} />
                                        <div className="solution-solution" dangerouslySetInnerHTML={{ __html: t('solutions.education.sceneB.solution') }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {/* Pricing Tab */}
                {activeTab === "pricing" && (
                    <section className="pricing-section tab-content">
                        <div className="pricing-header">
                            <p className="tab-slogan">{t('pricing.slogan')}</p>
                            <h2 className="section-title">{t('pricing.title')}</h2>
                        </div>

                        <div className="pricing-media">
                            <img src={PricingPlaceholder} alt="Pricing" />
                        </div>

                        <div className="pricing-rules">
                            <div className="pricing-rule">
                                <FiUsers className="pricing-rule-icon free" />
                                <h3>{t('pricing.humanFree')}</h3>
                            </div>
                            <div className="pricing-rule">
                                <FiCpu className="pricing-rule-icon paid" />
                                <h3>{t('pricing.aiPaid')}</h3>
                            </div>
                        </div>

                        <div className="pricing-plan">
                            <div className="pricing-plan-badge">{t('pricing.badge')}</div>
                            <h3 className="pricing-plan-name">{t('pricing.plan')}</h3>
                            <div className="pricing-plan-price">
                                <span className="pricing-price-amount">{t('pricing.price')}</span>
                                <span className="pricing-price-period">{t('pricing.period')}</span>
                            </div>
                            <div className="pricing-plan-features">
                                <div className="pricing-feature">
                                    <FiCheck className="pricing-feature-icon" />
                                    <span dangerouslySetInnerHTML={{ __html: t('pricing.feature1') }} />
                                </div>
                                <div className="pricing-feature">
                                    <FiCheck className="pricing-feature-icon" />
                                    <span>{t('pricing.feature2')}</span>
                                </div>
                                <div className="pricing-feature">
                                    <FiCheck className="pricing-feature-icon" />
                                    <span>{t('pricing.feature3')}</span>
                                </div>
                                <div className="pricing-feature">
                                    <FiCheck className="pricing-feature-icon" />
                                    <span>{t('pricing.feature4')}</span>
                                </div>
                            </div>
                            <div className="pricing-plan-cta">
                                <GoogleLoginButton />
                            </div>
                        </div>
                    </section>
                )}

                {/* Story Tab */}
                {activeTab === "story" && (
                    <section className="story-section tab-content">
                        <div className="theory-section">
                            <div className="theory-header">
                                <p className="tab-slogan">{t('story.theorySolgan')}</p>
                                <h2 className="section-title">{t('story.theoryTitle')}</h2>
                            </div>
                            <div className="story-media">
                                <img src={StoryPlaceholderTheory} alt="Core Theory" />
                            </div>
                            <div className="theory-pillars">
                                <div className="theory-pillar-card">
                                    <FaLightbulb className="theory-pillar-icon" />
                                    <h3>{t('story.ideaCentered.title')}</h3>
                                    <p className="theory-pillar-spirit">{t('story.ideaCentered.spirit')}</p>
                                    <p className="theory-pillar-description">{t('story.ideaCentered.desc')}</p>
                                </div>
                                <div className="theory-pillar-card">
                                    <FaSearch className="theory-pillar-icon" />
                                    <h3>{t('story.inquiry.title')}</h3>
                                    <p className="theory-pillar-spirit">{t('story.inquiry.spirit')}</p>
                                    <p className="theory-pillar-description">{t('story.inquiry.desc')}</p>
                                </div>
                                <div className="theory-pillar-card">
                                    <FaGraduationCap className="theory-pillar-icon" />
                                    <h3>{t('story.knowledgeBuilding.title')}</h3>
                                    <p className="theory-pillar-spirit">{t('story.knowledgeBuilding.spirit')}</p>
                                    <p className="theory-pillar-description">{t('story.knowledgeBuilding.desc')}</p>
                                </div>
                                <div className="theory-pillar-card">
                                    <FaHandshake className="theory-pillar-icon" />
                                    <h3>{t('story.argumentation.title')}</h3>
                                    <p className="theory-pillar-spirit">{t('story.argumentation.spirit')}</p>
                                    <p className="theory-pillar-description">{t('story.argumentation.desc')}</p>
                                </div>
                            </div>
                        </div>

                        <div className="story-header">
                            <p className="tab-slogan">{t('story.storySlogan')}</p>
                            <h2 className="section-title">{t('story.storyTitle')}</h2>
                        </div>

                        <div className="story-content">
                            <div className="story-block">
                                <div className="story-media">
                                    <img src={StoryPlaceholderLab} alt="From the Lab" />
                                </div>
                                <h3>{t('story.lab.title')}</h3>
                                <p>{t('story.lab.desc')}</p>
                            </div>

                            <div className="story-block">
                                <h3>{t('story.vision.title')}</h3>
                                <p dangerouslySetInnerHTML={{ __html: t('story.vision.desc') }} />
                            </div>

                            <div className="story-block contact">
                                <h3>{t('story.contact.title')}</h3>
                                <p>{t('story.contact.desc')}</p>
                                <a
                                    href="https://forms.gle/LL7KHYhhRxXjg4mf6"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="story-contact-button"
                                >
                                    {t('story.contact.cta')}
                                    <FiArrowRight className="story-contact-button-icon" />
                                </a>
                            </div>
                        </div>
                    </section>
                )}
            </div>

            <Login open={openLogin} setOpen={setOpenLogin} setOpenRegister={setOpenRegister} />
            <Register open={openRegister} setOpen={setOpenRegister} setOpenLogin={setOpenLogin} />
        </div>
    );
}
